import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

const portalRoot = typeof document !== "undefined" ? document.body : null;

const toneStyles = {
  warning: {
    iconBg: "tw-bg-amber-100 dark:tw-bg-amber-900/30",
    iconColor: "tw-text-amber-600 dark:tw-text-amber-300",
    buttonBg: "tw-bg-amber-600 hover:tw-bg-amber-700",
  },
  danger: {
    iconBg: "tw-bg-rose-100 dark:tw-bg-rose-900/30",
    iconColor: "tw-text-rose-600 dark:tw-text-rose-300",
    buttonBg: "tw-bg-rose-600 hover:tw-bg-rose-700",
  },
  info: {
    iconBg: "tw-bg-blue-100 dark:tw-bg-blue-900/30",
    iconColor: "tw-text-blue-600 dark:tw-text-blue-300",
    buttonBg: "tw-bg-blue-600 hover:tw-bg-blue-700",
  },
  success: {
    iconBg: "tw-bg-green-100 dark:tw-bg-green-900/30",
    iconColor: "tw-text-green-600 dark:tw-text-green-300",
    buttonBg: "tw-bg-green-600 hover:tw-bg-green-700",
  },
};

const icons = {
  warning: ExclamationTriangleIcon,
  danger: ExclamationTriangleIcon,
  info: InformationCircleIcon,
  success: CheckCircleIcon,
};

const AlertModal = ({
  isOpen,
  title = "Heads up",
  message = "",
  tone = "warning",
  onClose,
  onConfirm,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  hideCancel = false,
  loadingButton = false,
  noCloseAfterConfirm = false,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handler = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen || !portalRoot) {
    return null;
  }

  const toneStyle = toneStyles[tone] || toneStyles.warning;
  const Icon = icons[tone] || InformationCircleIcon;

  return createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-[9998] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/70 tw-backdrop-blur-sm">
      <div className="tw-relative tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-2xl tw-ring-1 tw-ring-slate-200 dark:tw-bg-slate-900 dark:tw-ring-slate-700">
        <button
          onClick={onClose}
          className="tw-absolute tw-right-3 tw-top-3 tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200"
        >
          <span className="tw-sr-only">Close</span>
          <XMarkIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
        </button>

        <div className="tw-flex tw-items-center tw-gap-4">
          <div className={`tw-rounded-2xl tw-p-3 ${toneStyle.iconBg}`}>
            <Icon className={`tw-h-6 tw-w-6 ${toneStyle.iconColor}`} />
          </div>
          <div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{title}</h3>
            <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-300">{message}</p>
          </div>
        </div>

        <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-3">
          {!hideCancel && (
            <button
              onClick={onClose}
              disabled={loadingButton}
              className="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50 dark:tw-border-slate-600 dark:tw-bg-slate-800 dark:tw-text-slate-200"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={() => {
              onConfirm?.();
              if (!noCloseAfterConfirm) {
                onClose?.();
              }
            }}
            disabled={loadingButton}
            className={`${toneStyle.buttonBg} tw-inline-flex tw-items-center tw-rounded-xl tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-transition-colors`}
          >
            {loadingButton ? "Loading..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    portalRoot
  );
};

AlertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tone: PropTypes.oneOf(["warning", "danger", "info", "success"]),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  hideCancel: PropTypes.bool,
};

export default AlertModal;
